<template>
  <div class="questionnaire-wrapper">
    <div class="question-wrapper">
      <div class="title-wrapper">
        <h2>Choose an option for the following statement?</h2>
      </div>
      <div class="screener-question">
        <h1>
          <Transition name="slide-fade" appear>
            <TextHighlighting
              :key="transitionKey"
              :textAudioUrl="currentQuestion?.questionUrl"
              :text="currentQuestion?.title"
              :hideUntilHighlight="true"
            />
          </Transition>
        </h1>
      </div>

      <div class="options-wrapper">
        <div class="options no-select">
          <div
            v-for="(option, index) in options"
            :key="index"
            ref="optionRefs"
            :class="[
              'option',
              {
                active: selectedOption === index
              }
            ]"
            @click="selectOption(index)"
          >
            {{ option }}
          </div>
          <div class="animation" :style="animationStyle" />
        </div>
      </div>

      <MButton
        class="next-button"
        :title="
          currentSubtestQuestionsLength === currentQuestion.index
            ? 'Finish Screener'
            : 'Next Question'
        "
        @click="onSelectOption"
        :icon="faArrowRight"
        v-visible="waitingForAnswer"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'

// Types
import type { QuestionEntity, OptionsEntity } from '@/customTypes'
import type { Ref, ComputedRef } from 'vue'

// Components
import TextHighlighting from '@/components/TextHighlighting.vue'
import MButton from '@/components/MButton.vue'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons'

// Stores
import { storeToRefs } from 'pinia'
import { useDataStore } from '@/stores/DataStore'

const dataStore = useDataStore()
const { currentSubtestQuestionsLength } = storeToRefs(dataStore)

const props = defineProps({
  currentQuestion: {
    type: Object as () => QuestionEntity,
    required: true
  },
  options: {
    type: Array,
    default: () => ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree']
  },
  isLastQuestion: {
    type: Boolean,
    default: false
  },
  waitingForAnswer: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits<{
  (e: 'answer-given'): void
}>()

const transitionKey: ComputedRef<number> = computed(() => props.currentQuestion.index || 0)
const defaultSelectedOption = Math.floor(props.options.length / 2)
const selectedOption: Ref<number> = ref(defaultSelectedOption) // Default to 'Neutral' or center position
const optionRefs = ref([])

const animationStyle = computed(() => {
  //Unused variables reposition the animation bar to the selected option on window resize
  //eslint-disable-next-line
  const { width, height } = windowSize.value
  if (!optionRefs.value[selectedOption.value]) return {}
  const selectedElement = optionRefs.value[selectedOption.value] as HTMLElement

  const rightSpacing = selectedOption.value === props.options.length - 1 ? 4 : 0
  const leftSpacing = selectedOption.value === 0 ? 3 : 0
  const left = selectedElement.offsetLeft + leftSpacing
  const widthValue = selectedElement.offsetWidth - rightSpacing

  return {
    width: `${widthValue}px`,
    left: `${left}px`
  }
})

onMounted(() => {
  window.addEventListener('resize', updateWindowSize)
  nextTick(() => {
    optionRefs.value = optionRefs.value.slice(0, props.options.length)

    setOptionSizing()
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateWindowSize)
})

const windowSize = ref({ width: window.innerWidth, height: window.innerHeight })

const updateWindowSize = () => {
  windowSize.value = {
    width: window.innerWidth,
    height: window.innerHeight
  }
}

const selectOption = (index: number) => {
  selectedOption.value = index
}

const onSelectOption = () => {
  if (!props.waitingForAnswer) return
  dataStore.onSelectAnswer({
    index: selectedOption.value,
    title: props.options[selectedOption.value] as string
  } as OptionsEntity)
  emit('answer-given')

  selectedOption.value = defaultSelectedOption
}

const setOptionSizing = () => {
  const firstOption: HTMLElement = optionRefs.value[0]
  const lastOption: HTMLElement = optionRefs.value[optionRefs.value.length - 1]

  const firstOptionWidth = firstOption.offsetWidth
  const lastOptionWidth = lastOption.offsetWidth
  const maxWidth = Math.max(firstOptionWidth, lastOptionWidth) + 10

  firstOption.style.width = `${maxWidth}px`
  lastOption.style.width = `${maxWidth}px`

  const remainingWidth = optionRefs.value.reduce(
    (total, option: HTMLElement, index) => {
      if (index !== 0 && index !== optionRefs.value.length - 1) {
        return total - option.offsetWidth
      }
      return total
    },
    firstOption.parentElement!.offsetWidth - maxWidth * 2
  )

  const middleOptionWidth = remainingWidth / 3

  optionRefs.value.forEach((option: HTMLElement, index) => {
    if (index !== 0 && index !== optionRefs.value.length - 1) {
      option.style.width = `${middleOptionWidth}px`
    }
  })
}
</script>

<style lang="scss">
.questionnaire-wrapper {
  height: 100%;
  display: flex;
  justify-content: center;
}

.question-wrapper {
  position: relative;
  margin: auto auto;
  width: calc(70ch * ((100vw - 400px) / 1200));
  max-width: calc(var(--full-vw) * 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: calc(var(--full-vh) * 0.1) 3rem;
}

.title-wrapper {
  width: 100%;
  margin: 0 auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  h2 {
    font-weight: 700;
    padding-bottom: 10px;
    border-bottom: 1px solid $grey;
    margin-bottom: calc(var(--full-vh) * 0.03);
    width: 100%;
    font-size: calc(20px * (100vw - 300px) / 1300);
  }
}

.question-wrapper h1 {
  font-size: calc(27px * (100vw - 300px) / 1300);
  display: flex;
  justify-content: center;
  min-height: 15vh;
  align-items: center;
}

.options-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-bottom: calc(var(--full-vh) * 0.01);
}

.options {
  margin: 50px auto 0;
  display: flex;
  justify-content: space-between;
  width: 100%;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 14px;
    background-color: #323444;
    border-radius: 8px;
    z-index: 1;
  }

  .option {
    font-weight: 300;
    line-height: 1.3rem;
    padding: 25px 0;
    text-align: center;
    color: #323444;
    cursor: pointer;
    position: relative;
    z-index: 1;
  }

  .animation {
    position: absolute;
    height: 8px;
    top: 3px;
    z-index: 2;
    transition: all 0.6s ease-out;
    background-color: $primary;
    border-radius: 8px;

    &::before {
      content: '';
      position: absolute;
      top: -2rem;
      width: 0.75rem;
      height: 0.75rem;
      margin: auto auto;
      background-color: $primary;
      border-radius: 50%;
      left: 50%;
      transform: translateX(-50%);
    }
  }
}

.option.active {
  font-weight: 800;
  color: $primary;
}

@media (max-height: 500px), (max-width: 500px) {
  .options {
    margin: calc(var(--full-vh) * 0.02) auto 0;
    .option {
      font-size: 0.8em;
    }
    .animation::before {
      content: none;
    }
  }

  .question-wrapper h1 {
    font-size: 1.5em;
  }

  .title-wrapper h2 {
    font-size: 1em;
  }
}

.next-button {
  visibility: hidden;
}
</style>
